import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StatusChip from '../common/StatusChip';
import { formatDate } from '../../utils/dateUtils';
import { getDisplayStatus } from '../../utils/enquiryDisplayUtils';

/**
 * Enquiry Details Popover Component
 * Displays complete enquiry information in a clean, readable layout.
 * When onEditClick and canEdit are set, shows Edit icon in header to open edit dialog.
 */
const EnquiryDetailsPopover = ({ enquiry, onEditClick, canEdit }) => {
  if (!enquiry) return null;

  const displayStatus = getDisplayStatus(enquiry);
  const showEdit = Boolean(onEditClick && (typeof canEdit === 'function' ? canEdit(enquiry) : canEdit));

  const formatRequirements = (requirements) => {
    if (!requirements || requirements.length === 0) {
      return 'No requirements specified';
    }
    return requirements.map((req, index) => (
      <Box
        key={index}
        sx={{
          mb: 1.5,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {req.item_name || 'Unnamed Item'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 0, sm: 1 },
            mt: 0.5,
            fontSize: '0.8125rem',
            color: 'text.secondary',
          }}
        >
          <span>Unit Price: ₹{(req.item_unit_price ?? 0).toLocaleString()}</span>
          <span>Quantity: {req.item_quantity ?? 0}</span>
          <span>Total Price: ₹{(req.item_total_price ?? 0).toLocaleString()}</span>
        </Box>
        {req.additional_details && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, whiteSpace: 'pre-wrap' }}>
            {req.additional_details}
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <Paper sx={{ p: 2, maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Enquiry Details
        </Typography>
        {showEdit && (
          <IconButton
            size="small"
            onClick={() => onEditClick(enquiry)}
            sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'action.hover' } }}
            aria-label="Edit enquiry"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Enquiry Number & Status */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Enquiry Number:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {enquiry.enquiryNumber || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Status:
          </Typography>
          <StatusChip status={displayStatus.label || enquiry.status} />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Client Information */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Client Information
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Typography variant="body2">
            <strong>Name:</strong> {enquiry.clientName || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Email:</strong> {enquiry.client_email || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Mobile:</strong> {enquiry.client_mobile || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Location:</strong> {enquiry.location || 'N/A'}
          </Typography>
        </Box>
      </Box>

      {enquiry.comment && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Additional Comment
            </Typography>
            <Box sx={{ pl: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {enquiry.comment}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Requirements */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          📦 Requirements
        </Typography>
        <Box sx={{ pl: 0 }}>
          {formatRequirements(enquiry.requirements || enquiry.requirement || [])}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Dates */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Important Dates
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Typography variant="body2">
            <strong>Enquiry Date:</strong> {formatDate(enquiry.initialEnquiryDate || enquiry.enquiry_date)}
          </Typography>
          <Typography variant="body2">
            <strong>Last Updated:</strong> {formatDate(enquiry.lastUpdatedOn)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: enquiry.next_follow_up_on
                ? (displayStatus.isMissed ? 'error.main' : 'inherit')
                : 'text.secondary',
              fontStyle: enquiry.next_follow_up_on ? 'normal' : 'italic',
            }}
          >
            <strong>Next Follow up Date:</strong>{' '}
            {enquiry.next_follow_up_on ? formatDate(enquiry.next_follow_up_on) : 'No Follow Up Date Added'}
          </Typography>
          {enquiry.createdAt && (
            <Typography variant="body2">
              <strong>Created:</strong> {formatDate(enquiry.createdAt)}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Assigned Employees */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Assignment
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Typography variant="body2">
            <strong>Current Assignee:</strong> {enquiry.assigned_employee_current_display || enquiry.assigned_employee_current || 'N/A'}
          </Typography>
          {enquiry.assigned_employees_till_date && enquiry.assigned_employees_till_date.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Previous Assignees:</strong> {enquiry.assigned_employees_till_date.join(', ')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Product Type */}
      {enquiry.main_product_type && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Product Type
            </Typography>
            <Box sx={{ pl: 1 }}>
              <Typography variant="body2">
                {enquiry.main_product_type}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default EnquiryDetailsPopover;
